/** @jsxImportSource @emotion/react */

import React, { useState } from 'react';
import styled from 'styled-components';
import Scroll from '@component/common/scroll';
import * as size from '@constant/size';
import { IS_IYAGIGO_MOBILE_VIEW } from '@config/environment';
import * as color from '@constant/color';
import TextBlock from '@component/common/block/text-block';
import Gap from '@component/common/gap';
import { v4 as uuid4 } from 'uuid';
import { useModifyStudentMutation } from '@hook/iyagigo/useApi';
import { useStudentKeyQuery } from '@hook/iyagigo/useApi';
import Calendar from '@component/common/calendar';
import { useNavigate } from 'react-router-dom';
import { useBeginStudy } from '@hook/iyagigo/useStudent';
import Line from '@component/common/line';
import IconBlock from '@component/common/block/icon-block';
import { actions as modalActions } from '@store/slice/modal-slice';
import { useTypedDispatch, useTypedSelector } from '@hook/common/useStore';
import { EStudyConfigurationMode, IStudyConfigurationModalData } from '@datatype/iyagigo/type';
import { EApiLanguageCode, EApiTest } from '@datatype/iyagigo/api-type';
import { IS_PROBLEM_SERVICE } from '@constant/variable';

// ############### variable ###############
const Wrapper = styled.div`
  position: relavite;
  width: 100%;
  height: 100%;
  padding: ${size.PAGE_PADDING};
`;

const KeyValueContainer = styled.div`
  display: flex;
  flex-flow: row nowrap;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  margin: 0 0 0 0;
  width: ${IS_IYAGIGO_MOBILE_VIEW ? '100%' : '200px'};
`;

const CountContainer = styled.div`
  display: flex;
  flex-flow: row nowrap;
  justify-content: flex-start;
  align-items: center;
  gap: 5px;
`;

const EditContainer = styled.div`
  display: flex;
  flex-flow: row nowrap;
  justify-content: flex-start;
  align-items: center;
  gap: 10px;
  cursor: pointer;
`;

const FOOTER_HEIGHT = IS_IYAGIGO_MOBILE_VIEW ? '40px' : '40px';
const FOOTER_GAP = IS_IYAGIGO_MOBILE_VIEW ? size.MARGIN_1 : size.MARGIN_2;

const INDEX_TEXT_DESIGN = {
  text: { size: 'medium', color: color.BLACK_2, bold: true },
  box: { alignX: 'left' },
} as const;

const KEY_TEXT_DESIGN = {
  text: { size: 'medium', color: color.BLACK_2, bold: false },
} as const;

const Configuration = () => {
  // ############### route ###############
  const navigate = useNavigate();
  // ############### state ###############
  const dispatch = useTypedDispatch();
  const [isTestEdit, setIsTestEdit] = useState<boolean>();
  const isAuthenticated = useTypedSelector((state) => state.rootReducer?.userReducer?.isAuthenticated) ?? false;
  // ############### api ###############
  const studentQuery = useStudentKeyQuery();
  const studyConfiguration = studentQuery.data?.data?.studyConfiguration;
  const mutation = useModifyStudentMutation();
  const studyLanguage = studyConfiguration?.studyLanguage;
  const englishVocabularyGoal = studyConfiguration?.englishVocabularyClozeGoal;
  const englishGrammarGoal = studyConfiguration?.englishGrammarClozeGoal;
  const japaneseVocabularyGoal = studyConfiguration?.japaneseVocabularyClozeGoal;
  const japaneseGrammarGoal = studyConfiguration?.japaneseGrammarClozeGoal;
  const jlptTestGoal = studyConfiguration?.jlpt5TestGoal;
  const toeicTestGoal = studyConfiguration?.toeic550TestGoal;
  const toeicTestLevel = studyConfiguration?.toeicTestLevel;
  const jlptTestLevel = studyConfiguration?.jlptTestLevel;

  const currentTestCodes = studyConfiguration?.testCodes || [];
  const currentStudyTests = currentTestCodes.map((test) => {
    switch (test) {
      case 'T1100':
        return 'JLPT N1';
      case 'T1200':
        return 'JLPT N2';
      case 'T1300':
        return 'JLPT N3';
      case 'T1400':
        return 'JLPT N4';
      case 'T1500':
        return 'JLPT N5';
      default:
        return '';
    }
  }) as string[];

  const [nextStudyTests, setNextStudyTests] = useState<string[]>(currentStudyTests);
  const nextStudyTestCodes = nextStudyTests.map((test) => {
    switch (test) {
      case 'JLPT N1':
        return 'T1100';
      case 'JLPT N2':
        return 'T1200';
      case 'JLPT N3':
        return 'T1300';
      case 'JLPT N4':
        return 'T1400';
      case 'JLPT N5':
        return 'T1500';
      default:
        return '';
    }
  }) as string[];

  // ############### handler ###############
  const handleEditClick = (type: EStudyConfigurationMode) => {
    dispatch(
      modalActions.showDialogModal({
        type: 'study-configuration',
        data: { mode: type },
      }),
    );
  };

  // ############### component ###############
  const tests = ['JLPT N1', 'JLPT N2', 'JLPT N3', 'JLPT N4', 'JLPT N5'];
  const StudyTests = tests.map((test) => {
    const isCurrentTest = (isTestEdit ? nextStudyTests : currentStudyTests).includes(test);
    return (
      <TextBlock
        key={uuid4()}
        text={test}
        design={{
          text: { size: 'tiny', color: color.BLACK_2 },
          box: {
            width: '60px',
            height: '25px',
            backgroundColor: isTestEdit ? 'none' : isCurrentTest ? color.YELLOW_1 : color.GRAY_3,
            borderWidth: isTestEdit ? `${size.BORDER_WIDTH_2}` : '0px',
            borderColor: isCurrentTest ? color.YELLOW_1 : color.GRAY_3,
            borderRadius: '5px',
            event: { cursor: isTestEdit ? 'pointer' : 'auto' },
          },
        }}
        click={{ handler: () => (isTestEdit ? setNextStudyTests([test]) : null) }}
      />
    );
  });

  // ############### function ###############
  const convertTestNameByApiTest = (test: EApiTest | null) => {
    switch (test) {
      case EApiTest.TOEIC950:
        return 'TOEIC 900점';
      case EApiTest.TOEIC850:
        return 'TOEIC 800점';
      case EApiTest.TOEIC750:
        return 'TOEIC 700점';
      case EApiTest.TOEIC650:
        return 'TOEIC 600점';
      case EApiTest.TOEIC550:
        return 'TOEIC 500점';
      case EApiTest.JLPT1:
        return 'JLPT N1';
      case EApiTest.JLPT2:
        return 'JLPT N2';
      case EApiTest.JLPT3:
        return 'JLPT N3';
      case EApiTest.JLPT4:
        return 'JLPT N4';
      case EApiTest.JLPT5:
        return 'JLPT N5';
      default:
        return '';
    }
  };

  return (
    <Wrapper>
      <TextBlock text={'시작 학습언어'} design={INDEX_TEXT_DESIGN} />
      <Line color={color.GRAY_3} margin={`${size.MARGIN_1} 0 ${size.MARGIN_1} 0`} />
      <EditContainer onClick={() => handleEditClick(EStudyConfigurationMode.STUDY_LANGUAGE)}>
        <CountContainer>
          <TextBlock
            text={studyLanguage === EApiLanguageCode.ENGLISH ? '🇺🇸 영어' : '🇯🇵 일본어'}
            design={{
              text: { size: 'medium', color: color.BLACK_2, bold: false },
              box: { alignX: 'left' },
            }}
          />
        </CountContainer>
        <IconBlock
          icon={'edit'}
          design={{
            icon: { size: 'tiny', kind: 'outlined', color: color.GRAY_EDIT_COLOR },
          }}
        />
      </EditContainer>
      <Gap height={size.MARGIN_5} />
      <TextBlock text={'오늘의 영어'} design={INDEX_TEXT_DESIGN} />
      <Line color={color.GRAY_3} margin={`${size.MARGIN_1} 0 ${size.MARGIN_1} 0`} />
      <KeyValueContainer>
        <TextBlock text={'어휘 일일목표'} design={KEY_TEXT_DESIGN} />
        <EditContainer onClick={() => handleEditClick(EStudyConfigurationMode.STUDY_ENGLISH_VOCABULARY_COUNT)}>
          <CountContainer>
            <TextBlock
              text={`${englishVocabularyGoal}`}
              design={{
                text: { size: 'medium', color: color.HIGHLIGHT_TEXT_COLOR, bold: true },
                box: { alignX: 'left' },
              }}
            />
            <TextBlock
              text={'개'}
              design={{
                text: { size: 'medium', color: color.BLACK_2, bold: false },
                box: { alignX: 'left' },
              }}
            />
          </CountContainer>
          <IconBlock
            icon={'edit'}
            design={{
              icon: { size: 'tiny', kind: 'outlined', color: color.GRAY_EDIT_COLOR },
            }}
          />
        </EditContainer>
      </KeyValueContainer>
      <Gap height={size.MARGIN_2} />
      <KeyValueContainer>
        <TextBlock text={'문법 일일목표'} design={KEY_TEXT_DESIGN} />
        <EditContainer onClick={() => handleEditClick(EStudyConfigurationMode.STUDY_ENGLISH_GRAMMAR_COUNT)}>
          <CountContainer>
            <TextBlock
              text={`${englishGrammarGoal}`}
              design={{
                text: { size: 'medium', color: color.HIGHLIGHT_TEXT_COLOR, bold: true },
                box: { alignX: 'left' },
              }}
            />
            <TextBlock
              text={'개'}
              design={{
                text: { size: 'medium', color: color.BLACK_2, bold: false },
                box: { alignX: 'left' },
              }}
            />
          </CountContainer>
          <IconBlock
            icon={'edit'}
            design={{
              icon: { size: 'tiny', kind: 'outlined', color: color.GRAY_EDIT_COLOR },
            }}
          />
        </EditContainer>
      </KeyValueContainer>
      <Gap height={size.MARGIN_5} />
      {IS_PROBLEM_SERVICE && (
        <>
          <TextBlock text={'영어 시험'} design={INDEX_TEXT_DESIGN} />
          <Line color={color.GRAY_3} margin={`${size.MARGIN_1} 0 ${size.MARGIN_1} 0`} />
          <KeyValueContainer>
            <TextBlock text={'학습 시험'} design={KEY_TEXT_DESIGN} />
            <EditContainer onClick={() => handleEditClick(EStudyConfigurationMode.STUDY_ENGLISH_TEST_LEVEL)}>
              <CountContainer>
                <TextBlock
                  text={`${convertTestNameByApiTest(toeicTestLevel || null)}`}
                  design={{
                    text: { size: 'medium', color: color.HIGHLIGHT_TEXT_COLOR, bold: true },
                    box: { alignX: 'left' },
                  }}
                />
              </CountContainer>
              <IconBlock
                icon={'edit'}
                design={{
                  icon: { size: 'tiny', kind: 'outlined', color: color.GRAY_EDIT_COLOR },
                }}
              />
            </EditContainer>
          </KeyValueContainer>
          <Gap height={size.MARGIN_2} />
          <KeyValueContainer>
            <TextBlock text={'회차 일일목표'} design={KEY_TEXT_DESIGN} />
            <EditContainer onClick={() => handleEditClick(EStudyConfigurationMode.STUDY_ENGLISH_TEST_COUNT)}>
              <CountContainer>
                <TextBlock
                  text={`${toeicTestGoal}`}
                  design={{
                    text: { size: 'medium', color: color.HIGHLIGHT_TEXT_COLOR, bold: true },
                    box: { alignX: 'left' },
                  }}
                />
                <TextBlock
                  text={'회'}
                  design={{
                    text: { size: 'medium', color: color.BLACK_2, bold: false },
                    box: { alignX: 'left' },
                  }}
                />
              </CountContainer>
              {/* <IconBlock
              icon={'edit'}
              design={{
                icon: { size: 'tiny', kind: 'outlined', color: color.GRAY_EDIT_COLOR },
              }}
            /> */}
            </EditContainer>
          </KeyValueContainer>
          <Gap height={size.MARGIN_4} />
        </>
      )}
      <TextBlock text={'오늘의 일본어'} design={INDEX_TEXT_DESIGN} />
      <Line color={color.GRAY_3} margin={`${size.MARGIN_1} 0 ${size.MARGIN_1} 0`} />
      <KeyValueContainer>
        <TextBlock text={'어휘 일일목표'} design={KEY_TEXT_DESIGN} />
        <EditContainer onClick={() => handleEditClick(EStudyConfigurationMode.STUDY_JAPANESE_VOCABULARY_COUNT)}>
          <CountContainer>
            <TextBlock
              text={`${japaneseVocabularyGoal}`}
              design={{
                text: { size: 'medium', color: color.HIGHLIGHT_TEXT_COLOR, bold: true },
                box: { alignX: 'left' },
              }}
            />
            <TextBlock
              text={'개'}
              design={{
                text: { size: 'medium', color: color.BLACK_2, bold: false },
                box: { alignX: 'left' },
              }}
            />
          </CountContainer>
          <IconBlock
            icon={'edit'}
            design={{
              icon: { size: 'tiny', kind: 'outlined', color: color.GRAY_EDIT_COLOR },
            }}
          />
        </EditContainer>
      </KeyValueContainer>
      <Gap height={size.MARGIN_2} />
      <KeyValueContainer>
        <TextBlock text={'문법 일일목표'} design={KEY_TEXT_DESIGN} />
        <EditContainer onClick={() => handleEditClick(EStudyConfigurationMode.STUDY_JAPANESE_GRAMMAR_COUNT)}>
          <CountContainer>
            <TextBlock
              text={`${japaneseGrammarGoal}`}
              design={{
                text: { size: 'medium', color: color.HIGHLIGHT_TEXT_COLOR, bold: true },
                box: { alignX: 'left' },
              }}
            />
            <TextBlock
              text={'개'}
              design={{
                text: { size: 'medium', color: color.BLACK_2, bold: false },
                box: { alignX: 'left' },
              }}
            />
          </CountContainer>
          <IconBlock
            icon={'edit'}
            design={{
              icon: { size: 'tiny', kind: 'outlined', color: color.GRAY_EDIT_COLOR },
            }}
          />
        </EditContainer>
      </KeyValueContainer>
      <Gap height={size.MARGIN_5} />
      {IS_PROBLEM_SERVICE && (
        <>
          <TextBlock text={'일본어 시험'} design={INDEX_TEXT_DESIGN} />
          <Line color={color.GRAY_3} margin={`${size.MARGIN_1} 0 ${size.MARGIN_1} 0`} />
          <KeyValueContainer>
            <TextBlock text={'학습 시험'} design={KEY_TEXT_DESIGN} />
            <EditContainer onClick={() => handleEditClick(EStudyConfigurationMode.STUDY_JAPANESE_TEST_LEVEL)}>
              <CountContainer>
                <TextBlock
                  text={`${convertTestNameByApiTest(jlptTestLevel || null)}`}
                  design={{
                    text: { size: 'medium', color: color.HIGHLIGHT_TEXT_COLOR, bold: true },
                    box: { alignX: 'left' },
                  }}
                />
              </CountContainer>
              <IconBlock
                icon={'edit'}
                design={{
                  icon: { size: 'tiny', kind: 'outlined', color: color.GRAY_EDIT_COLOR },
                }}
              />
            </EditContainer>
          </KeyValueContainer>
          <Gap height={size.MARGIN_2} />
          <KeyValueContainer>
            <TextBlock text={'회차 일일목표'} design={KEY_TEXT_DESIGN} />
            <EditContainer onClick={() => handleEditClick(EStudyConfigurationMode.STUDY_JAPANESE_TEST_COUNT)}>
              <CountContainer>
                <TextBlock
                  text={`${jlptTestGoal}`}
                  design={{
                    text: { size: 'medium', color: color.HIGHLIGHT_TEXT_COLOR, bold: true },
                    box: { alignX: 'left' },
                  }}
                />
                <TextBlock
                  text={'회'}
                  design={{
                    text: { size: 'medium', color: color.BLACK_2, bold: false },
                    box: { alignX: 'left' },
                  }}
                />
              </CountContainer>
              {/* <IconBlock
              icon={'edit'}
              design={{
                icon: { size: 'tiny', kind: 'outlined', color: color.GRAY_EDIT_COLOR },
              }}
            /> */}
            </EditContainer>
          </KeyValueContainer>
        </>
      )}
    </Wrapper>
  );
};

export default Configuration;
